import React, { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";
import {
  getRoleDetail,
  updateRole,
  clearRoleErrors,
} from "../../../actions/adminRoleActions";
import { UPDATE_ROLE_RESET } from "../../../constants/roleConstants";
import { getPermissionGroups } from "../../../utils/permissions";
import Loader from "../../Loader";

const permissionGroups = getPermissionGroups();

const EditRole = ({ history, match }) => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const roleId = match.params.id;

  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [isSystem, setIsSystem] = useState(false);

  const { role, loading: detailLoading } = useSelector(
    (state) => state.roleDetail || {}
  );
  const { isUpdated, loading: updateLoading, error } = useSelector(
    (state) => state.updateRole || {}
  );

  useEffect(() => {
    dispatch(getRoleDetail(roleId));
  }, [dispatch, roleId]);

  useEffect(() => {
    if (role) {
      setName(role.name || "");
      setLabel(role.label || "");
      setDescription(role.description || "");
      setSelectedPermissions(role.permissions || []);
      setIsSystem(role.isSystem || false);
    }
  }, [role]);

  useEffect(() => {
    if (error) {
      alert.error(error);
      dispatch(clearRoleErrors());
    }
    if (isUpdated) {
      alert.success("Role updated successfully");
      history.push("/admin/roles");
      dispatch({ type: UPDATE_ROLE_RESET });
    }
  }, [dispatch, isUpdated, alert, error, history]);

  const handlePermissionToggle = (perm) => {
    setSelectedPermissions((prev) =>
      prev.includes(perm)
        ? prev.filter((p) => p !== perm)
        : [...prev, perm]
    );
  };

  const handleGroupToggle = (groupPerms) => {
    const allSelected = groupPerms.every((p) =>
      selectedPermissions.includes(p)
    );
    if (allSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((p) => !groupPerms.includes(p))
      );
    } else {
      setSelectedPermissions((prev) => [
        ...new Set([...prev, ...groupPerms]),
      ]);
    }
  };

  const handleSelectAll = () => {
    const allPerms = Object.values(permissionGroups).flat();
    if (selectedPermissions.length === allPerms.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(allPerms);
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    dispatch(
      updateRole(roleId, {
        label: label.trim() || name.trim(),
        description: description.trim(),
        permissions: selectedPermissions,
      })
    );
  };

  const loading = detailLoading || updateLoading;
  const allPerms = Object.values(permissionGroups).flat();

  return (
    <Fragment>
      {loading ? (
        <Loader />
      ) : (
        <Fragment>
          <div className="content-wrapper">
            <section className="content-header">
              <h1>
                Edit <small>Role</small>
              </h1>
              <ol className="breadcrumb">
                <li>
                  <Link to="/admin">
                    <i className="fa fa-dashboard"></i> Dashboard
                  </Link>
                </li>
                <li>
                  <Link to="/admin/roles">
                    <i className="fa fa-shield"></i> Roles
                  </Link>
                </li>
                <li className="active">Edit Role</li>
              </ol>
            </section>

            <section className="content">
              <div className="box box-default">
                <div className="box-header with-border">
                  <h3 className="box-title">
                    Edit Role: <strong>{name}</strong>
                    {isSystem && (
                      <span
                        className="label bg-yellow"
                        style={{ marginLeft: 8 }}
                      >
                        System Role
                      </span>
                    )}
                  </h3>
                </div>
                <form onSubmit={submitHandler}>
                  <div className="box-body">
                    <div className="form-group row">
                      <div className="col-md-4">
                        <label htmlFor="name">Role Name</label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          value={name}
                          disabled
                          readOnly
                        />
                        <small className="text-muted">
                          Name cannot be changed
                        </small>
                      </div>
                      <div className="col-md-4">
                        <label htmlFor="label">Display Label</label>
                        <input
                          type="text"
                          className="form-control"
                          id="label"
                          placeholder="e.g. Content Editor"
                          value={label}
                          onChange={(e) => setLabel(e.target.value)}
                        />
                      </div>
                      <div className="col-md-4">
                        <label htmlFor="description">Description</label>
                        <input
                          type="text"
                          className="form-control"
                          id="description"
                          placeholder="Optional description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </div>
                    </div>

                    <hr />
                    <h4>
                      Permissions{" "}
                      <small className="text-muted">
                        ({selectedPermissions.length} / {allPerms.length}{" "}
                        selected)
                      </small>
                      <button
                        type="button"
                        className="btn btn-xs btn-default pull-right"
                        onClick={handleSelectAll}
                      >
                        {selectedPermissions.length === allPerms.length
                          ? "Deselect All"
                          : "Select All"}
                      </button>
                    </h4>

                    <div className="row">
                      {Object.entries(permissionGroups).map(
                        ([group, perms]) => {
                          const allChecked = perms.every((p) =>
                            selectedPermissions.includes(p)
                          );
                          return (
                            <div
                              className="col-md-4 col-sm-6"
                              key={group}
                              style={{ marginBottom: 16 }}
                            >
                              <div
                                className="box box-solid box-default"
                                style={{ marginBottom: 0 }}
                              >
                                <div
                                  className="box-header with-border"
                                  style={{ padding: "6px 10px" }}
                                >
                                  <label
                                    style={{
                                      margin: 0,
                                      cursor: "pointer",
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={allChecked}
                                      onChange={() =>
                                        handleGroupToggle(perms)
                                      }
                                      style={{ marginRight: 6 }}
                                    />
                                    {group}
                                  </label>
                                </div>
                                <div
                                  className="box-body"
                                  style={{ padding: "6px 10px" }}
                                >
                                  {perms.map((perm) => {
                                    const action = perm.split(".")[1];
                                    return (
                                      <div key={perm}>
                                        <label
                                          style={{
                                            fontWeight: "normal",
                                            cursor: "pointer",
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={selectedPermissions.includes(
                                              perm
                                            )}
                                            onChange={() =>
                                              handlePermissionToggle(
                                                perm
                                              )
                                            }
                                            style={{ marginRight: 6 }}
                                          />
                                          {action}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                  <div className="box-footer">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={updateLoading}
                    >
                      {updateLoading ? "Updating..." : "Update Role"}
                    </button>
                    <Link
                      to="/admin/roles"
                      className="btn btn-default"
                      style={{ marginLeft: 8 }}
                    >
                      Cancel
                    </Link>
                  </div>
                </form>
              </div>
            </section>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default EditRole;
